// internal-deps
import { OrderApiData, Response } from "@swiggy-private/payment-types";
import { DashOrderResponse } from "@payment-client/interfaces/OrderPublic";
import { API_SUCCESS_STATUS_CODE as SUCCESS_API_RESPONSE } from "@swiggy-private/core-utils";
import { isBusinessLineDineout } from "@swiggy-private/checkout-core";
import { isOrderConfirmed } from "./PublicOrderUtils";
import { getConfirmationTimeInMinutes, getSwiggyMoneyPollingIntervalInMs } from "./SwiggyInstrumentsHelper";

// interfaces

/**
 * Transforms the dash order response into response understood by the
 * web payment client.
 *
 * @param response {@Response}
 * @returns {@Response}
 */
export function transformOrderResponse(
    response: Response<DashOrderResponse>,
    businessline: string,
): Response<DashOrderResponse | OrderApiData> {
    if (!response.data || response.statusCode != SUCCESS_API_RESPONSE) {
        return response;
    }

    const data = response.data;

    if (!data.orders || data.orders.length === 0) {
        return response;
    }

    const order = data.orders[0];
    const confirmOrderEnabledXp = response.data.xpData?.confirmOrderEnabled || false;
    const orderJob = order.orderJobs[0];
    const orderMeta = (() => {
        try {
            return JSON.parse(orderJob.metadata);
        } catch (error) {
            return {};
        }
    })();
    const { appRedirectionInfo, targetedMarketplacePaymentForwardReferenceId: targetedMarketplacePaymentReferenceId } =
        data?.paymentDetails ?? {};

    const paymentInfo = Array.isArray(data?.transactionPaymentDetails)
        ? data?.transactionPaymentDetails?.[0]
        : {
              paymentTxnStatus: "pending",
          };

    const paymentStatus = paymentInfo.paymentTxnStatus.toLowerCase();
    const status = isBusinessLineDineout() ? orderJob.extendedStatus : orderJob.status;
    const isConfirmed = isOrderConfirmed(businessline, status);

    const orderData = {
        orderId: orderJob.orderJobId,
        orderType: orderMeta.type,
        orderTotal: orderJob?.paymentInfo ? orderJob?.paymentInfo?.[0].transactionAmount : "",
        orderItems:
            orderMeta.items?.map((i: { quantity: number }) => ({
                quantity: i.quantity,
            })) || [],
        orderPaymentMethod: orderJob?.paymentInfo ? orderJob?.paymentInfo?.[0]?.paymentMethod : "",
        postStatus: isConfirmed ? "processing" : paymentStatus,
        orderStatus: isConfirmed ? "processing" : paymentStatus,
        successMessageInfo: orderMeta.postOrderScreenMessageText,
        // TODO: remove below line once paymentPartnerOrderId is available in paymentInfo
        paymentPartnerOrderId: paymentInfo?.paymentPartnerOrderId ?? paymentInfo?.paymentTxnId,
        newDashConfirmOrderEnabledXp: confirmOrderEnabledXp, // XP key for iM new confirm order flow
        newDineoutConfirmOrderEnabledXp: confirmOrderEnabledXp, // XP key for Dineout new confirm order flow
        // Transforming dash returned polling duration to food polling duration contract
        confirmationTimeMinutes: paymentInfo.confirmationTimeInMinutes,
        ...paymentInfo,

        // above payment fields would stop flowing after a while. all of them would now be part of redirection_info object
        // so we are not modifying above things and instead would consume redirection info on client and do backfilling
        // of these missing keys.
        // Added after payment data localization, now all payment info flows under these 2 keys.
        appRedirectionInfo,
        targetedMarketplacePaymentReferenceId,
        orderTransitionScreenNewMsgEnabled: response.data.orderTransitionScreenNewMsgEnabled,
        deliveryAddress: response.data.deliveryAddress,
    };

    if (paymentInfo?.amazonPayResponse && paymentInfo?.amazonPayResponse?.amazonPayLoad) {
        orderData.paymentMethodMeta = paymentInfo?.amazonPayResponse?.amazonPayLoad;
    }

    const responseToReturn = {
        ...response,
        data: {
            ...response.data,
            orderData,
        },
    };

    if (responseToReturn.rawResponse && responseToReturn.rawResponse.data) {
        /* eslint-disable @typescript-eslint/naming-convention */
        responseToReturn.rawResponse.data.order_data = {
            ...responseToReturn.rawResponse.data,
            order_id: orderJob.orderJobId,
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    return responseToReturn;
}

/**
 * Transforms the dash order response into response understood by the
 * web payment client.
 *
 * @param response {@Response}
 * @returns {@Response}
 */
export function transformDashOrderResponse(
    response: Response<DashOrderResponse>,
    businessline: string,
): Response<DashOrderResponse | OrderApiData> {
    return transformOrderResponse(response, businessline);
}

/**
 * Transforms the swiggy money order response into response understood by the
 * web payment client.
 *
 * @param response {@Response}
 * @returns {@Response}
 */
export function transformSwiggyMoneyOrderResponse(response: Response<OrderApiData>): Response<OrderApiData> {
    return {
        ...response,
        data: response.data
            ? {
                  ...response.data,
                  orderData: response.data?.orderData
                      ? {
                            ...response.data?.orderData,
                            confirmationTimeMinutes: String(getConfirmationTimeInMinutes(response.data?.orderData)),
                            pollingIntervalInMs: getSwiggyMoneyPollingIntervalInMs(response.data?.orderData),
                        }
                      : null,
              }
            : null,
    };
}

export function transformSwiggySuperOrderResponse(response: Response<OrderApiData>): Response<OrderApiData> {
    return {
        ...response,
        data: response.data
            ? {
                  ...response.data,
                  orderData: response.data?.orderData
                      ? {
                            ...response.data?.orderData,
                            confirmationTimeMinutes: String(getConfirmationTimeInMinutes(response.data?.orderData)),
                        }
                      : null,
              }
            : null,
    };
}
